import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { dataGridSx } from '../themes/dataGridSx';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockResetIcon from '@mui/icons-material/LockReset';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Auth from '@aws-amplify/auth';
import { useSelector, useDispatch } from 'react-redux';
import { selectGroups, selectGroupsLoaded, setGroups } from '../features/auth/authSlice';
import { listUsers, setUserStatus, deleteUser, resetPassword, listGroups } from '../services/adminService';
import { getAllUserAssets } from '../services';
import AddUserModal from '../components/modals/AddUserModal';
import EditUserModal from '../components/modals/EditUserModal';
import { GROUP_DOMAIN_RESTRICTIONS } from '../constants/groupDomainRestrictions';

const AdminPortal = () => {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationToken, setPaginationToken] = useState(null);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [userAssetsMap, setUserAssetsMap] = useState({});

  const dispatch = useDispatch();
  const loggedInGroups = useSelector(selectGroups);
  const groupsLoaded = useSelector(selectGroupsLoaded);
  const isAdmin = loggedInGroups.includes('ADMINS');
  const isGroupAdmin = !isAdmin && loggedInGroups.some(g => g !== 'ADMINS');
  const canManageUsers = isAdmin || isGroupAdmin;
  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  useEffect(() => {
    if (loggedInGroups.length > 0 && selectedGroup === '') {
      setSelectedGroup(loggedInGroups.includes('ADMINS') ? 'ADMINS' : loggedInGroups[0]);
    }
  }, [loggedInGroups]);

  const handleGroupChange = (group) => {
    setSelectedGroup(group);
  };

  const displayedUsers = (() => {
    if (!groupsLoaded) return [];
    if (isAdmin && (!selectedGroup || selectedGroup === 'ADMINS')) return users;
    if (selectedGroup) {
      return users.filter(u =>
        (userAssetsMap[u.email] ?? []).some(a => a.AdminGroupName === selectedGroup)
      );
    }
    // selectedGroup not yet initialized but groups are known — filter by all their groups
    if (isGroupAdmin && loggedInGroups.length > 0) {
      return users.filter(u =>
        (userAssetsMap[u.email] ?? []).some(a => loggedInGroups.includes(a.AdminGroupName))
      );
    }
    return users;
  })();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const [usersSettled, cognitoSettled, assetsSettled, groupsSettled] = await Promise.allSettled([
        listUsers(60, null),
        Auth.currentAuthenticatedUser(), // reads local cache only — no Hub events, no loop risk
        getAllUserAssets(),
        listGroups()
      ]);

      if (usersSettled.status === 'rejected') throw usersSettled.reason;
      const response = usersSettled.value;
      const currentCognitoUser = cognitoSettled.status === 'fulfilled' ? cognitoSettled.value : null;
      const assetsResult = assetsSettled.status === 'fulfilled' ? assetsSettled.value : null;
      if (groupsSettled.status === 'fulfilled') setAllGroups(groupsSettled.value ?? []);
      setUsers(response.users);
      setPaginationToken(response.paginationToken);

      const map = {};
      (assetsResult?.data ?? []).forEach(asset => {
        const email = asset.email;
        if (!map[email]) map[email] = [];
        map[email].push(asset);
      });
      setUserAssetsMap(map);

      // Sync groups from Cognito-fresh listUsers data instead of the stale JWT
      const currentEmail = currentCognitoUser?.attributes?.email;
      if (currentEmail) {
        const me = response.users.find(u => u.email === currentEmail);
        if (me?.groups?.length > 0) {
          dispatch(setGroups(me.groups));
        }
      }

      setSnackbarInfo({
        open: true,
        message: `Loaded ${response.users.length} users`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error loading users:', error);
      setSnackbarInfo({
        open: true,
        message: 'Failed to load users',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadUsers();
  };

  const handleOpenAddUserModal = () => {
    setAddUserModalOpen(true);
  };

  const handleCloseAddUserModal = () => {
    setAddUserModalOpen(false);
  };

  const handleUserCreated = () => {
    setAddUserModalOpen(false);
    loadUsers();
    setSnackbarInfo({
      open: true,
      message: 'User created successfully',
      severity: 'success'
    });
  };

  const handleOpenEditUserModal = (user) => {
    setUserToEdit(user);
    setEditUserModalOpen(true);
  };

  const handleCloseEditUserModal = () => {
    setEditUserModalOpen(false);
    setUserToEdit(null);
  };

  const handleUserUpdated = () => {
    setEditUserModalOpen(false);
    setUserToEdit(null);
    loadUsers();
    setSnackbarInfo({
      open: true,
      message: 'User updated successfully',
      severity: 'success'
    });
  };

  const handleOpenStatusDialog = (user) => {
    setSelectedUser(user);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedUser(null);
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedUser) return;

    setStatusChangeLoading(true);

    try {
      const result = await setUserStatus(
        selectedUser.username,
        !selectedUser.enabled
      );

      if (result.success) {
        setSnackbarInfo({
          open: true,
          message: `User ${!selectedUser.enabled ? 'enabled' : 'disabled'} successfully`,
          severity: 'success'
        });

        handleCloseConfirmDialog();
        loadUsers();
      } else {
        setSnackbarInfo({
          open: true,
          message: result.message || 'Failed to update user status',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      setSnackbarInfo({
        open: true,
        message: 'Failed to update user status',
        severity: 'error'
      });
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarInfo({ ...snackbarInfo, open: false });
  };

  // Reset Password handlers
  const handleOpenResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setResetPasswordDialogOpen(true);
  };

  const handleCloseResetPasswordDialog = () => {
    setResetPasswordDialogOpen(false);
    setSelectedUser(null);
  };

  const handleConfirmResetPassword = async () => {
    if (!selectedUser) return;

    setActionLoading(true);

    try {
      const result = await resetPassword(selectedUser.username);

      if (result.success) {
        setSnackbarInfo({
          open: true,
          message: result.message || `Password reset email sent to ${selectedUser.email || selectedUser.username}`,
          severity: 'success'
        });
        handleCloseResetPasswordDialog();
      } else {
        setSnackbarInfo({
          open: true,
          message: result.message || 'Failed to reset password',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      setSnackbarInfo({
        open: true,
        message: 'Failed to reset password',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Delete User handlers
  const handleOpenDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;

    setActionLoading(true);

    try {
      const result = await deleteUser(selectedUser.username);

      if (result.success) {
        setSnackbarInfo({
          open: true,
          message: `User ${selectedUser.username} deleted successfully`,
          severity: 'success'
        });
        handleCloseDeleteDialog();
        loadUsers();
      } else {
        setSnackbarInfo({
          open: true,
          message: result.message || 'Failed to delete user',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbarInfo({
        open: true,
        message: 'Failed to delete user',
        severity: 'error'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const renderStatusBadge = (params) => {
    const status = params.value;

    const statusColors = {
      'CONFIRMED': '#4caf50',
      'FORCE_CHANGE_PASSWORD': '#ff9800',
      'UNCONFIRMED': '#9e9e9e'
    };

    const color = statusColors[status] || '#78909c';

    return (
      <Chip
        label={status.replace(/_/g, ' ')}
        size="small"
        sx={{
          backgroundColor: alpha(color, 0.12),
          color: color,
          fontWeight: 500,
          borderRadius: '4px',
          fontSize: '0.75rem',
          height: '24px',
          '& .MuiChip-label': { px: 1.5 }
        }}
      />
    );
  };

  const renderEnabledStatus = (params) => {
    const enabled = params.value;

    return (
      <Chip
        label={enabled ? 'Active' : 'Disabled'}
        size="small"
        icon={enabled ? <CheckCircleIcon /> : <BlockIcon />}
        sx={{
          backgroundColor: enabled ? alpha('#4caf50', 0.12) : alpha('#f44336', 0.12),
          color: enabled ? '#4caf50' : '#f44336',
          fontWeight: 500,
          borderRadius: '4px',
          fontSize: '0.75rem',
          height: '24px',
          '& .MuiChip-label': { px: 1.5 },
          '& .MuiChip-icon': { fontSize: '0.875rem', ml: 0.5 }
        }}
      />
    );
  };

  const renderGroups = (params) => {
    if (!params.value || params.value.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {params.value.map((group, index) => (
          <Chip
            key={index}
            label={group}
            size="small"
            sx={{
              backgroundColor: 'surface.subtle',
              border: '1px solid',
              borderColor: 'divider',
              color: 'text.primary',
              fontSize: '0.75rem',
              height: '22px'
            }}
          />
        ))}
      </Box>
    );
  };

  const renderCreatedDate = (params) => {
    if (!params.value) return null;

    const date = new Date(params.value);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    return (
      <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
        {formattedDate}
      </Typography>
    );
  };

  const renderActions = (params) => {
    const user = params.row;

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
        <Tooltip title="Edit User">
          <IconButton
            size="small"
            onClick={() => handleOpenEditUserModal(user)}
            sx={{
              color: 'info.main',
              p: 0.5,
              '&:hover': { backgroundColor: alpha('#1976d2', 0.1) }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title={user.enabled ? 'Disable User' : 'Enable User'}>
          <IconButton
            size="small"
            onClick={() => handleOpenStatusDialog(user)}
            sx={{
              color: user.enabled ? 'error.main' : 'success.main',
              p: 0.5,
              '&:hover': { backgroundColor: alpha(user.enabled ? '#f44336' : '#4caf50', 0.1) }
            }}
          >
            {user.enabled ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Reset Password">
          <IconButton
            size="small"
            onClick={() => handleOpenResetPasswordDialog(user)}
            sx={{
              color: 'warning.main',
              p: 0.5,
              '&:hover': { backgroundColor: alpha('#ff9800', 0.1) }
            }}
          >
            <LockResetIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete User">
          <IconButton
            size="small"
            onClick={() => handleOpenDeleteDialog(user)}
            sx={{
              color: 'error.main',
              p: 0.5,
              '&:hover': { backgroundColor: alpha('#d32f2f', 0.1) }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderLicenses = (params) => {
    const assets = userAssetsMap[params.row.email] ?? [];
    if (assets.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          None
        </Typography>
      );
    }
    const today = new Date();
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
        {assets.map((asset) => {
          const exp = asset.ExpirationDate;
          const isNeverExpires = !exp || exp.startsWith('2099');
          const expDate = exp ? new Date(exp) : null;
          const daysUntilExpiry = expDate ? Math.ceil((expDate - today) / (1000 * 60 * 60 * 24)) : Infinity;

          let chipColor = '#04aeea';
          let chipBg = 'rgba(4,174,234,0.1)';
          if (!isNeverExpires && daysUntilExpiry < 0) {
            chipColor = '#ef4444';
            chipBg = 'rgba(239,68,68,0.1)';
          } else if (!isNeverExpires && daysUntilExpiry <= 30) {
            chipColor = '#f97316';
            chipBg = 'rgba(249,115,22,0.1)';
          }

          const tooltipTitle = isNeverExpires
            ? `${asset.AccountName} — No expiration`
            : `${asset.AccountName} — Expires ${new Date(exp).toLocaleDateString()}`;

          return (
            <Tooltip key={asset.pkAccountLicenseID} title={tooltipTitle} arrow placement="top">
              <Chip
                label={asset.AccountName}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  color: chipColor,
                  backgroundColor: chipBg,
                  border: `1px solid ${chipColor}`,
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    );
  };

  const columns = [
    // {
    //   field: 'username',
    //   headerName: 'Username',
    //   width: 150,
    //   renderCell: (params) => (
    //     <Typography variant="body2" fontWeight="medium">
    //       {params.value}
    //     </Typography>
    //   )
    // },
    {
      field: 'email',
      headerName: 'Email',
      width: 220,
      flex: 1
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 180
    },
    // {
    //   field: 'phoneNumber',
    //   headerName: 'Phone',
    //   width: 150
    // },
    {
      field: 'enabled',
      headerName: 'Status',
      width: 120,
      renderCell: renderEnabledStatus,
      headerAlign: 'center',
      align: 'center'
    },
    {
      field: 'status',
      headerName: 'Account Status',
      width: 180,
      renderCell: renderStatusBadge
    },
    {
      field: 'groups',
      headerName: 'Groups',
      width: 120,
      renderCell: renderGroups
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 130,
      renderCell: renderCreatedDate
    },
    {
      field: 'licenses',
      headerName: 'Licenses',
      width: 260,
      sortable: false,
      filterable: false,
      renderCell: renderLicenses
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: renderActions,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center'
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ height: '90vh', py: 3 }}>
      <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3, boxShadow: 2, borderRadius: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AdminPanelSettingsIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1">
                User Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Manage user accounts and permissions
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {(isAdmin ? allGroups : loggedInGroups).length > 0 && (
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <Select
                  value={selectedGroup}
                  onChange={(e) => handleGroupChange(e.target.value)}
                  disabled={loading}
                  sx={{
                    fontSize: '0.85rem',
                    backgroundColor: alpha('#04aeea', 0.08),
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '&:hover': { backgroundColor: alpha('#04aeea', 0.15) },
                  }}
                >
                  {(isAdmin ? allGroups : loggedInGroups).map(group => (
                    <MenuItem key={group} value={group}>{group}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Tooltip title="Refresh">
              <IconButton
                color="primary"
                onClick={handleRefresh}
                disabled={loading}
                sx={{
                  backgroundColor: alpha('#04aeea', 0.08),
                  '&:hover': { backgroundColor: alpha('#04aeea', 0.15) }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {canManageUsers && (
              <Tooltip title="Add User">
                <IconButton
                  color="primary"
                  onClick={handleOpenAddUserModal}
                  sx={{
                    backgroundColor: alpha('#04aeea', 0.08),
                    '&:hover': { backgroundColor: alpha('#04aeea', 0.15) }
                  }}
                >
                  <PersonAddIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* DataGrid */}
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <DataGridPro
            sx={{ ...dataGridSx(theme), height: '100%' }}
            rows={displayedUsers}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.username}
            disableRowSelectionOnClick
            initialState={{
              sorting: {
                sortModel: [{ field: 'created', sort: 'desc' }]
              }
            }}
            getRowHeight={() => 'auto'}
            rowBufferPx={400}
            slots={{
              loadingOverlay: () => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              )
            }}
          />
        </Box>
      </Paper>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarInfo.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarInfo.severity} sx={{ width: '100%' }}>
          {snackbarInfo.message}
        </Alert>
      </Snackbar>
      {/* Add User Modal */}
      <AddUserModal
        open={addUserModalOpen}
        onClose={handleCloseAddUserModal}
        onUserCreated={handleUserCreated}
        allowedDomains={isAdmin ? [] : (GROUP_DOMAIN_RESTRICTIONS[selectedGroup] ?? [])}
      />
      {/* Edit User Modal */}
      <EditUserModal
        open={editUserModalOpen}
        onClose={handleCloseEditUserModal}
        onUserUpdated={handleUserUpdated}
        user={userToEdit}
        isAdmin={isAdmin}
      />
      {/* Confirmation Dialog for Disable/Enable User */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedUser?.enabled ? 'Disable User Account?' : 'Enable User Account?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedUser?.enabled
              ? `Are you sure you want to disable ${selectedUser?.email || selectedUser?.username}? They will not be able to log in.`
              : `Are you sure you want to enable ${selectedUser?.email || selectedUser?.username}? They will be able to log in.`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={statusChangeLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmStatusChange}
            color={selectedUser?.enabled ? 'error' : 'success'}
            variant="contained"
            disabled={statusChangeLoading}
          >
            {statusChangeLoading ? 'Processing...' : (selectedUser?.enabled ? 'Disable' : 'Enable')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirmation Dialog for Reset Password */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={handleCloseResetPasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset User Password?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedUser?.status === 'FORCE_CHANGE_PASSWORD'
              ? <>A new invitation email with a temporary password will be sent to <strong>{selectedUser?.email || selectedUser?.username}</strong>.</>
              : <>A password reset email will be sent to <strong>{selectedUser?.email || selectedUser?.username}</strong>. The user will receive instructions to create a new password.</>
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetPasswordDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmResetPassword}
            color="primary"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Sending...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirmation Dialog for Delete User */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete User Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete <strong>{selectedUser?.email || selectedUser?.username}</strong>?
            This action cannot be undone and all user data will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPortal;
